import { useState, useCallback } from 'react';
import { Timestamp } from 'firebase/firestore';
import { useAuthContext } from '../components/AuthProvider';
import { CanvasMetadata, CloudSaveResult, CloudLoadResult, CloudSaveListResult } from '../services/cloudSave/canvasMetadata';
import { trackEvent } from '../utils/analytics';

interface CloudSaveState {
  loading: boolean;
  error: string | null;
  userCanvases: CanvasMetadata[];
  lastSavedCanvas: CanvasMetadata | null;
}

export const useCloudSave = () => {
  const { user } = useAuthContext();
  const [state, setState] = useState<CloudSaveState>({
    loading: false,
    error: null,
    userCanvases: [],
    lastSavedCanvas: null
  });

  // Clear error
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // Save canvas to cloud
  const saveCanvas = useCallback(async (
    content: string,
    canvasName: string,
    canvasId?: string,
    description?: string,
    tags?: string[]
  ): Promise<CloudSaveResult> => {
    if (!user) {
      return {
        success: false,
        error: 'You must be logged in to save to cloud'
      };
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const { CloudSaveService } = await import('../services/cloudSave/cloudSaveService');
      const result = await CloudSaveService.saveCanvas(
        content,
        canvasName,
        user.uid,
        canvasId,
        description,
        tags
      );

      if (result.success && result.metadata) {
        setState(prev => ({
          ...prev,
          lastSavedCanvas: result.metadata || null,
          loading: false
        }));

        // Track successful save
        trackEvent('canvas_saved_to_cloud', {
          canvas_id: result.canvasId,
          canvas_name: canvasName,
          file_size: result.metadata.fileSize,
          is_update: !!canvasId
        });

        // Refresh user canvases list
        await loadUserCanvases();
      } else {
        setState(prev => ({
          ...prev,
          error: result.error || 'Failed to save canvas',
          loading: false
        }));

        // Track failed save
        trackEvent('canvas_save_failed', {
          error: result.error,
          canvas_name: canvasName
        });
      }

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setState(prev => ({
        ...prev,
        error: errorMessage,
        loading: false
      }));

      // Track error
      trackEvent('canvas_save_error', {
        error: errorMessage,
        canvas_name: canvasName
      });

      return {
        success: false,
        error: errorMessage
      };
    }
  }, [user]);

  // Load canvas from cloud
  const loadCanvas = useCallback(async (canvasId: string): Promise<CloudLoadResult> => {
    if (!user) {
      return {
        success: false,
        error: 'You must be logged in to load from cloud'
      };
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const { CloudSaveService } = await import('../services/cloudSave/cloudSaveService');
      const result = await CloudSaveService.loadCanvas(canvasId, user.uid);

      if (result.success) {
        setState(prev => ({ ...prev, loading: false }));

        // Track successful load
        trackEvent('canvas_loaded_from_cloud', {
          canvas_id: canvasId,
          canvas_name: result.metadata?.name,
          file_size: result.metadata?.fileSize
        });
      } else {
        setState(prev => ({
          ...prev,
          error: result.error || 'Failed to load canvas',
          loading: false
        }));

        // Track failed load
        trackEvent('canvas_load_failed', {
          canvas_id: canvasId,
          error: result.error
        });
      }

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setState(prev => ({
        ...prev,
        error: errorMessage,
        loading: false
      }));

      // Track error
      trackEvent('canvas_load_error', {
        canvas_id: canvasId,
        error: errorMessage
      });

      return {
        success: false,
        error: errorMessage
      };
    }
  }, [user]);

  // Load user's canvases list
  const loadUserCanvases = useCallback(async (): Promise<CloudSaveListResult> => {
    if (!user) {
      return {
        success: false,
        error: 'You must be logged in to load canvases'
      };
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const { CloudSaveService } = await import('../services/cloudSave/cloudSaveService');
      const result = await CloudSaveService.listUserCanvases(user.uid);

      if (result.success) {
        setState(prev => ({
          ...prev,
          userCanvases: result.canvases || [],
          loading: false
        }));

        // Track successful list load
        trackEvent('user_canvases_loaded', {
          canvas_count: result.canvases?.length || 0
        });
      } else {
        setState(prev => ({
          ...prev,
          error: result.error || 'Failed to load canvases',
          loading: false
        }));

        // Track failed list load
        trackEvent('user_canvases_load_failed', {
          error: result.error
        });
      }

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setState(prev => ({
        ...prev,
        error: errorMessage,
        loading: false
      }));

      // Track error
      trackEvent('user_canvases_load_error', {
        error: errorMessage
      });

      return {
        success: false,
        error: errorMessage
      };
    }
  }, [user]);

  // Delete canvas from cloud
  const deleteCanvas = useCallback(async (canvasId: string): Promise<CloudSaveResult> => {
    if (!user) {
      return {
        success: false,
        error: 'You must be logged in to delete canvases'
      };
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const { CloudSaveService } = await import('../services/cloudSave/cloudSaveService');
      const result = await CloudSaveService.deleteCanvas(canvasId, user.uid);

      if (result.success) {
        setState(prev => ({
          ...prev,
          loading: false,
          // Remove from local list
          userCanvases: prev.userCanvases.filter(canvas => canvas.id !== canvasId)
        }));

        // Track successful deletion
        trackEvent('canvas_deleted_from_cloud', {
          canvas_id: canvasId
        });
      } else {
        setState(prev => ({
          ...prev,
          error: result.error || 'Failed to delete canvas',
          loading: false
        }));

        // Track failed deletion
        trackEvent('canvas_delete_failed', {
          canvas_id: canvasId,
          error: result.error
        });
      }

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setState(prev => ({
        ...prev,
        error: errorMessage,
        loading: false
      }));

      // Track error
      trackEvent('canvas_delete_error', {
        canvas_id: canvasId,
        error: errorMessage
      });

      return {
        success: false,
        error: errorMessage
      };
    }
  }, [user]);

  // Update canvas metadata
  const updateCanvasMetadata = useCallback(async (
    canvasId: string,
    updates: {
      name?: string;
      description?: string;
      tags?: string[];
    }
  ): Promise<CloudSaveResult> => {
    if (!user) {
      return {
        success: false,
        error: 'You must be logged in to update canvases'
      };
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const { CloudSaveService } = await import('../services/cloudSave/cloudSaveService');
      const result = await CloudSaveService.updateCanvasMetadata(canvasId, {
        ...updates,
        lastModified: Timestamp.now()
      });

      if (result.success && result.metadata) {
        setState(prev => ({
          ...prev,
          loading: false,
          // Update in local list
          userCanvases: prev.userCanvases.map(canvas => 
            canvas.id === canvasId ? result.metadata! : canvas
          )
        }));

        // Track successful update
        trackEvent('canvas_metadata_updated', {
          canvas_id: canvasId,
          updated_fields: Object.keys(updates)
        });
      } else {
        setState(prev => ({
          ...prev,
          error: result.error || 'Failed to update canvas',
          loading: false
        }));

        // Track failed update
        trackEvent('canvas_metadata_update_failed', {
          canvas_id: canvasId,
          error: result.error
        });
      }

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setState(prev => ({
        ...prev,
        error: errorMessage,
        loading: false
      }));

      // Track error
      trackEvent('canvas_metadata_update_error', {
        canvas_id: canvasId,
        error: errorMessage
      });

      return {
        success: false,
        error: errorMessage
      };
    }
  }, [user]);

  return {
    // State
    loading: state.loading,
    error: state.error,
    userCanvases: state.userCanvases,
    lastSavedCanvas: state.lastSavedCanvas,

    // Actions
    saveCanvas,
    loadCanvas,
    loadUserCanvases,
    deleteCanvas,
    updateCanvasMetadata,
    clearError
  };
};
